import { ArchitectureParseError } from "../errors";
import type { Risk, RiskCategory, RiskResponseKind, RiskSeverity } from "../types";
import { asString } from "./parse-bullets-v3";

// Coerce js-yaml output into structured Risk[]. The schema document lists
// canonical categories / severities, but we accept anything and coerce
// unknowns to `other` / `info` rather than throwing — the parser stays
// forgiving while still surfacing the canonical values to the renderer.

const CATEGORY_VALUES: ReadonlySet<RiskCategory> = new Set([
  "cost",
  "scalability",
  "security",
  "compliance",
  "data-readiness",
  "data-connectivity",
  "operability",
  "vendor-lock-in",
  "other",
]);

const SEVERITY_VALUES: ReadonlySet<RiskSeverity> = new Set(["info", "warning", "critical"]);

const RESPONSE_KINDS: ReadonlySet<RiskResponseKind> = new Set([
  "mitigation",
  "alternative",
  "accepted_because",
]);

interface ParseContext {
  /** Path-like label for error messages, e.g. "container:order-api". */
  scope: string;
  /**
   * When true, any malformed risk (missing response block, multiple
   * response kinds, etc.) throws ArchitectureParseError. When false, the
   * risk is dropped silently and the next one is tried — useful when the
   * mapper is being lenient. v0.3 parser passes strict=true.
   */
  strict: boolean;
}

export function parseRisks(value: unknown, ctx: ParseContext): Risk[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  const out: Risk[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const entry = value[i];
    const risk = coerceRisk(entry, { ...ctx, scope: `${ctx.scope}.risks[${i}]` });
    if (risk) out.push(risk);
  }
  return out;
}

function coerceRisk(value: unknown, ctx: ParseContext): Risk | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    if (ctx.strict) {
      throwParseError(
        `${ctx.scope}: expected a risk object (category, severity, description, response).`,
      );
    }
    return null;
  }
  const rec = value as Record<string, unknown>;
  const description = asString(rec.description) ?? "";
  if (description === "") {
    if (ctx.strict) {
      throwParseError(`${ctx.scope}: \`description\` is required and must be non-empty.`);
    }
    return null;
  }
  const response = coerceResponse(rec.response, ctx);
  if (!response) return null;
  const rawCategory = (asString(rec.category) ?? "other").toLowerCase();
  const category: RiskCategory = CATEGORY_VALUES.has(rawCategory as RiskCategory)
    ? (rawCategory as RiskCategory)
    : "other";
  const category_label = category === "other" && rawCategory !== "other" ? rawCategory : undefined;
  const rawSeverity = (asString(rec.severity) ?? "info").toLowerCase();
  const severity: RiskSeverity = SEVERITY_VALUES.has(rawSeverity as RiskSeverity)
    ? (rawSeverity as RiskSeverity)
    : "info";
  return {
    category,
    category_label,
    severity,
    description,
    response,
  };
}

function coerceResponse(
  value: unknown,
  ctx: ParseContext,
): { kind: RiskResponseKind; text: string } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    if (ctx.strict) {
      throwParseError(
        `${ctx.scope}: \`response\` block missing. Each risk must have exactly one of \`mitigation\`, \`alternative\`, or \`accepted_because\`.`,
      );
    }
    return null;
  }
  const rec = value as Record<string, unknown>;
  const keys = Object.keys(rec).filter((k) => RESPONSE_KINDS.has(k as RiskResponseKind));
  if (keys.length === 0) {
    if (ctx.strict) {
      throwParseError(
        `${ctx.scope}: \`response\` block must contain one of \`mitigation\`, \`alternative\`, or \`accepted_because\`. Found keys: ${Object.keys(rec).join(", ") || "(empty)"}.`,
      );
    }
    return null;
  }
  if (keys.length > 1) {
    if (ctx.strict) {
      throwParseError(
        `${ctx.scope}: \`response\` block must contain EXACTLY one of \`mitigation\`, \`alternative\`, or \`accepted_because\`. Found ${keys.length} (${keys.join(", ")}). Pick one — risks are never simultaneously mitigated AND accepted-as-is.`,
      );
    }
    return null;
  }
  const kind = keys[0] as RiskResponseKind;
  const text = asString(rec[kind]) ?? "";
  if (text === "") {
    if (ctx.strict) {
      throwParseError(`${ctx.scope}: \`response.${kind}\` is required and must be non-empty.`);
    }
    return null;
  }
  return { kind, text };
}

function throwParseError(message: string): never {
  throw new ArchitectureParseError(message);
}
